import { z } from "zod";
import { authoringCreatedBySchema, authoringPersistedIdSchema, authoringSlugSchema, authoringVersionStatusSchema, } from "./authoring-shared.js";
export const skillKindSchema = z.enum([
    "analysis",
    "retrieval",
    "report",
    "ingest",
]);
export const skillParentStatusSchema = z.enum(["active", "archived"]);
export const skillCapabilitySchema = z.enum([
    "readonly-data",
    "writeback",
    "web-research",
    "browser",
    "terminal",
]);
const triggerPhraseSchema = z.string().trim().min(1).max(120);
const dataSourceSchema = z.string().trim().min(1).max(120);
const outputModeSchema = z.string().trim().min(1).max(60);
export const createSkillInputSchema = z.object({
    slug: authoringSlugSchema,
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).default(""),
    routingDescription: z.string().trim().max(600).default(""),
    kind: skillKindSchema,
    markdown: z.string().min(1),
    triggerPhrases: z.array(triggerPhraseSchema).max(64).default([]),
    allowedDataSources: z.array(dataSourceSchema).max(32).default([]),
    requiredCapabilities: z.array(skillCapabilitySchema).max(8).default([]),
    requiredAmbientContextVersionIds: z
        .array(authoringPersistedIdSchema)
        .max(32)
        .default([]),
    outputMode: outputModeSchema.default("markdown"),
    fallbackSkillSlugs: z.array(authoringSlugSchema).max(16).default([]),
    writesKnowledge: z.boolean().default(false),
    createdBy: authoringCreatedBySchema.default("web"),
});
export const createSkillDraftVersionInputSchema = z.object({
    slug: authoringSlugSchema,
    copyFromVersionId: authoringPersistedIdSchema.optional(),
    createdBy: authoringCreatedBySchema.default("web"),
});
export const updateSkillDraftInputSchema = z.object({
    versionId: authoringPersistedIdSchema,
    markdown: z.string().min(1).optional(),
    triggerPhrases: z.array(triggerPhraseSchema).max(64).optional(),
    allowedDataSources: z.array(dataSourceSchema).max(32).optional(),
    requiredCapabilities: z.array(skillCapabilitySchema).max(8).optional(),
    requiredAmbientContextVersionIds: z
        .array(authoringPersistedIdSchema)
        .max(32)
        .optional(),
    outputMode: outputModeSchema.optional(),
    fallbackSkillSlugs: z.array(authoringSlugSchema).max(16).optional(),
    writesKnowledge: z.boolean().optional(),
});
export const updateSkillMetaInputSchema = z.object({
    slug: authoringSlugSchema,
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(1000).optional(),
    routingDescription: z.string().trim().max(600).optional(),
    kind: skillKindSchema.optional(),
    status: skillParentStatusSchema.optional(),
});
export const publishSkillDraftInputSchema = z.object({
    versionId: authoringPersistedIdSchema,
});
export const unpublishSkillInputSchema = z.object({
    slug: authoringSlugSchema,
});
export const deleteSkillInputSchema = z.object({
    slug: authoringSlugSchema,
});
export const deleteSkillVersionInputSchema = z.object({
    versionId: authoringPersistedIdSchema,
});
// Read-view shapes
export const skillVersionViewSchema = z.object({
    id: z.string(),
    skillId: z.string(),
    version: z.number(),
    status: authoringVersionStatusSchema,
    markdown: z.string(),
    triggerPhrases: z.array(z.string()),
    allowedDataSources: z.array(z.string()),
    requiredCapabilities: z.array(skillCapabilitySchema),
    requiredAmbientContextVersionIds: z.array(z.string()),
    outputMode: z.string(),
    fallbackSkillSlugs: z.array(z.string()),
    writesKnowledge: z.boolean(),
    publishedAt: z.number().optional(),
    createdBy: z.string(),
    createdAt: z.number(),
});
export const skillParentViewSchema = z.object({
    id: z.string(),
    slug: z.string(),
    tenantSlug: z.string().optional(),
    name: z.string(),
    description: z.string(),
    routingDescription: z.string(),
    kind: skillKindSchema,
    status: skillParentStatusSchema,
    currentPublishedVersionId: z.string().optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const skillListItemViewSchema = z.object({
    skill: skillParentViewSchema,
    currentPublished: skillVersionViewSchema.nullable(),
    versionCount: z.number(),
});
export const skillDetailViewSchema = z.object({
    skill: skillParentViewSchema,
    versions: z.array(skillVersionViewSchema),
    currentPublished: skillVersionViewSchema.nullable(),
});
// AGE-15: SDK shapes for the tenant-owned skills surface.
// `source` distinguishes a row that lives in the caller's tenant
// (`"tenant"`) from one resolved out of the `__mlx__` system tenant
// (`"global"`). Returned in list/get views so callers can tell at a
// glance whether they own the skill or are seeing a MLX-shipped global.
export const skillSourceSchema = z.enum(["tenant", "global"]);
// `scope=all` (default) merges the caller's tenant with `__mlx__` globals,
// caller wins on slug collision. `own` skips globals; `globals` returns
// only `__mlx__`.
export const listSkillsScopeSchema = z
    .enum(["own", "globals", "all"])
    .default("all");
export const listSkillsQueryParamsSchema = z.object({
    scope: listSkillsScopeSchema,
});
// `getSkill` only supports `own` (skip global fallback) or `all` (default,
// fall back to `__mlx__`). `globals` would be ambiguous at this granularity.
export const getSkillScopeSchema = z.enum(["own", "all"]).default("all");
export const getSkillQueryParamsSchema = z.object({
    scope: getSkillScopeSchema,
});
export const skillListItemWithSourceSchema = skillListItemViewSchema.extend({
    source: skillSourceSchema,
});
export const listSkillsResponseSchema = z.array(skillListItemWithSourceSchema);
export const getSkillResponseSchema = skillDetailViewSchema.extend({
    source: skillSourceSchema,
});
// `createSkill` body is `createSkillInputSchema` (existing). Response
// includes the parent + initial v1 draft summary.
export const createSkillResponseSchema = z.object({
    skillId: z.string(),
    versionId: z.string(),
    version: z.number(),
});
// `patchSkill` body — `updateSkillMetaInputSchema` minus `slug` (slug is
// now a path param). Same field rules for everything else.
export const patchSkillInputSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(1000).optional(),
    routingDescription: z.string().trim().max(600).optional(),
    kind: skillKindSchema.optional(),
    status: skillParentStatusSchema.optional(),
});
export const patchSkillResponseSchema = z.object({
    skillId: z.string(),
});
export const deleteSkillResponseSchema = z.object({
    deleted: z.boolean(),
    versionsDeleted: z.number(),
});
// `createSkillDraftVersion` body — existing schema minus `slug` (path param).
export const createSkillDraftVersionPathInputSchema = z.object({
    copyFromVersionId: authoringPersistedIdSchema.optional(),
    createdBy: authoringCreatedBySchema.default("web"),
});
export const createSkillDraftVersionResponseSchema = z.object({
    versionId: z.string(),
    version: z.number(),
});
// `patchSkillVersion` body — `updateSkillDraftInputSchema` minus
// `versionId` (now derived from the {slug, version} path).
export const patchSkillVersionInputSchema = z.object({
    markdown: z.string().min(1).optional(),
    triggerPhrases: z.array(triggerPhraseSchema).max(64).optional(),
    allowedDataSources: z.array(dataSourceSchema).max(32).optional(),
    requiredCapabilities: z.array(skillCapabilitySchema).max(8).optional(),
    requiredAmbientContextVersionIds: z
        .array(authoringPersistedIdSchema)
        .max(32)
        .optional(),
    outputMode: outputModeSchema.optional(),
    fallbackSkillSlugs: z.array(authoringSlugSchema).max(16).optional(),
    writesKnowledge: z.boolean().optional(),
});
export const patchSkillVersionResponseSchema = z.object({
    versionId: z.string(),
});
export const deleteSkillVersionResponseSchema = z.object({
    deleted: z.boolean(),
});
// `publishSkillVersion` body — explicit version number (the {slug} path
// param + the version body field together identify the draft).
export const publishSkillVersionPathInputSchema = z.object({
    version: z.number().int().positive(),
});
export const publishSkillVersionResponseSchema = z.object({
    skillId: z.string(),
    versionId: z.string(),
});
export const unpublishSkillResponseSchema = z.object({
    skillId: z.string(),
    unpublished: z.boolean(),
});
// Path-param schemas for OpenAPI emission and SDK typing.
export const skillSlugPathParamSchema = authoringSlugSchema;
// Version is a positive integer in the URL; emit as a string and the
// handler coerces.
export const skillVersionPathParamSchema = z
    .string()
    .regex(/^[1-9][0-9]*$/, "Version must be a positive integer");
